import React, { cloneElement, ReactNode } from 'react';

import { cn } from 'support/helpers/cn/cn';

import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip';

export enum BadgeColor {
  gray = 'gray',
  red = 'red',
  yellow = 'yellow',
  green = 'green',
  blue = 'blue',
  indigo = 'indigo',
  purple = 'purple',
  pink = 'pink',
}

type BadgeProps = {
  icon?: React.ReactNode;
  type?: 'basic' | 'indicator' | 'filters';
  color?: BadgeColor;
  hasDot?: boolean;
  onRemove?: () => void;
  children: React.ReactNode;
  className?: string;
  tooltip?: string | ReactNode;
};

const classes = {
  color: {
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    red: 'bg-red-50 border-red-100 text-red-800',
    yellow: 'bg-yellow-50 border-yellow-100 text-yellow-800',
    green: 'bg-green-50 border-green-100 text-green-800',
    blue: 'bg-blue-50 border-blue-100 text-blue-800',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-800',
    purple: 'bg-purple-50 border-purple-100 text-purple-800',
    pink: 'bg-pink-50 border-pink-100 text-pink-800',
  },
  dotColor: {
    gray: 'text-gray-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    green: 'text-green-400',
    blue: 'text-blue-400',
    indigo: 'text-indigo-400',
    purple: 'text-purple-400',
    pink: 'text-pink-400',
  },
  dotShadowColor: {
    gray: 'bg-gray-100',
    red: 'bg-red-50',
    yellow: 'bg-yellow-50',
    green: 'bg-green-50',
    blue: 'bg-blue-50',
    indigo: 'bg-indigo-50',
    purple: 'bg-purple-50',
    pink: 'bg-pink-50',
  },
  type: {
    basic: 'border',
    indicator: 'bg-transparent!',
    filters: 'border bg-gray-50 border-gray-200 text-gray-800',
  },
};

export function Badge({
  icon,
  type = 'basic',
  color = BadgeColor.gray,
  hasDot = type === 'indicator',
  onRemove,
  className,
  children,
  tooltip,
}: BadgeProps) {
  return (
    <Tooltip>
      <TooltipTrigger>
        <span
          className={cn(
            'inline-flex items-center rounded-full py-0.5 text-xs font-medium',
            type !== 'filters' ? classes.color[color] : '',
            classes.type[type],
            onRemove ? 'pr-0.5 pl-2' : 'px-2',
            className ?? '',
          )}
        >
          <div
            className={cn(
              'relative',
              hasDot ? ' mr-1.5 size-2' : '',
              type === 'indicator' ? cn(classes.dotShadowColor[color], 'mr-1.5 size-4 rounded-full') : '',
            )}
          >
            {hasDot && (
              <svg
                data-testid="badge-dot"
                className={cn('absolute top-1/2 left-1/2 size-2 -translate-1/2', classes.dotColor[color])}
                fill="currentColor"
                viewBox="0 0 8 8"
              >
                <circle cx={4} cy={4} r={3} />
              </svg>
            )}
          </div>
          {icon && cloneElement(icon as React.ReactElement<{ className?: string }>, { className: 'w-4 h-4 mr-1.5' })}
          <span className="truncate leading-4">{children}</span>
          {!!onRemove && (
            <button
              type="button"
              data-testid="badge-remove"
              onClick={onRemove}
              className={cn(
                'ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full text-gray-600 hover:text-gray-500 focus-visible:bg-gray-500 focus-visible:text-white focus-visible:outline-hidden',
              )}
            >
              <span className="sr-only">Remove</span>
              <svg className="size-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
              </svg>
            </button>
          )}
        </span>
      </TooltipTrigger>
      {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  );
}
